import { makeStyles } from '@material-ui/core';
import { boschGray } from '../../modules/theme/boschColors';

/**
 * Visual spacing applied above + below the rule. We expose a tiny
 * enum instead of free-form pixels so the YAML stays terse and a few
 * admins editing different pages don't end up with rules at 17px,
 * 18px, 20px — all visually identical but inconsistent in code review.
 */
export type DividerSpacing = 'tight' | 'normal' | 'loose';
export type DividerStyle = 'solid' | 'dashed';

const SPACING_MAP: Record<DividerSpacing, number> = {
  tight: 4,
  normal: 16,
  loose: 32,
};

const useStyles = makeStyles(() => ({
  /*
   * `hr` because that's the semantic element for a thematic break.
   * Material UI's `<Divider>` works too but ships with a faint grey
   * that doesn't match the rest of the Bosch palette under our
   * theme overrides. Hand-rolling keeps the colour predictable.
   */
  rule: {
    border: 'none',
    width: '100%',
    height: 0,
  },
}));

export type DividerProps = {
  spacing?: DividerSpacing;
  style?: DividerStyle;
};

/**
 * Plain horizontal rule for splitting a page without the extra
 * weight of `section-header`. Useful when the next block speaks for
 * itself (a Markdown heading inside it, an Announcement, etc.) and
 * adding a second heading would feel like double-titling the area.
 */
export const Divider = ({
  spacing = 'normal',
  style = 'solid',
}: DividerProps) => {
  const classes = useStyles();
  const gap = SPACING_MAP[spacing] ?? SPACING_MAP.normal;
  return (
    <hr
      className={classes.rule}
      style={{
        marginTop: gap,
        marginBottom: gap,
        borderTop: `1px ${style} ${boschGray[85]}`,
      }}
    />
  );
};
